import type { Dayjs } from "dayjs";
import type { MilestoneNode } from "pages/design-package-details-page/components/view-dependencies/model";
import { DateUtils } from "utils/dateutils";

function dayOf(date: string): string {
  return DateUtils.dateTimeObj(date).startOf("day").format("YYYY-MM-DD");
}

function milestoneIndex(
  milestones: MilestoneNode[],
  milestoneId: string
): { ordered: MilestoneNode[]; index: number } {
  const ordered = milestonesInSequence(milestones);
  return { ordered, index: ordered.findIndex((m) => m.id === milestoneId) };
}

/** Latest committed date among milestones before this one, in project time. */
function latestPastCommittedDate(
  milestones: MilestoneNode[],
  milestoneId: string
): string | null {
  const { ordered, index } = milestoneIndex(milestones, milestoneId);
  let latest: string | null = null;
  for (let i = 0; i < index; i += 1) {
    const date = ordered[i].committedDate;
    if (!date) continue;
    if (
      !latest ||
      DateUtils.dateTimeObj(date).isAfter(DateUtils.dateTimeObj(latest))
    ) {
      latest = date;
    }
  }
  return latest;
}

/** Earliest committed date among milestones after this one, in project time. */
function earliestFutureCommittedDate(
  milestones: MilestoneNode[],
  milestoneId: string
): string | null {
  const { ordered, index } = milestoneIndex(milestones, milestoneId);
  let earliest: string | null = null;
  for (let i = index + 1; i < ordered.length; i += 1) {
    const date = ordered[i].committedDate;
    if (!date) continue;
    if (
      !earliest ||
      DateUtils.dateTimeObj(date).isBefore(DateUtils.dateTimeObj(earliest))
    ) {
      earliest = date;
    }
  }
  return earliest;
}

export function milestonesInSequence(
  milestones: MilestoneNode[]
): MilestoneNode[] {
  return [...milestones].sort((a, b) => a.sequenceNo - b.sequenceNo);
}

/**
 * Inclusive bounds for a milestone's committed date: not after the next
 * committed date, not before the previous one. Milestones with no committed
 * date are skipped, so a gap still keeps the sequence in order.
 */
export function committedDateBounds(
  milestones: MilestoneNode[],
  milestoneId: string
): { min: string | null; max: string | null } {
  const ordered = milestonesInSequence(milestones);
  const index = ordered.findIndex((m) => m.id === milestoneId);
  if (index < 0) return { min: null, max: null };

  let min: string | null = null;
  for (let i = index - 1; i >= 0; i -= 1) {
    if (ordered[i].committedDate) {
      min = dayOf(ordered[i].committedDate!);
      break;
    }
  }
  let max: string | null = null;
  for (let i = index + 1; i < ordered.length; i += 1) {
    if (ordered[i].committedDate) {
      max = dayOf(ordered[i].committedDate!);
      break;
    }
  }
  return { min, max };
}

export function isCommittedDayOutOfRange(
  day: string,
  bounds: { min: string | null; max: string | null }
): boolean {
  if (bounds.min && day < bounds.min) return true;
  if (bounds.max && day > bounds.max) return true;
  return false;
}

/** The latest actual day among the milestones, or null when none is set. */
export function latestActualDay(milestones: MilestoneNode[]): string | null {
  return milestones.reduce<string | null>((latest, milestone) => {
    if (!milestone.actualDate) return latest;
    const day = dayOf(milestone.actualDate);
    return !latest || day > latest ? day : latest;
  }, null);
}

/**
 * Actual dates fill in sequence. With none set, only the first milestone is
 * editable. Once one is set, that milestone and the next one are editable, so
 * the latest actual can still be changed or cleared and the following one can
 * be entered. Everything before the latest actual stays locked.
 */
export function isActualDateEditable(
  milestones: MilestoneNode[],
  milestoneId: string
): boolean {
  const ordered = milestonesInSequence(milestones);
  const index = ordered.findIndex((m) => m.id === milestoneId);
  if (index < 0) return false;
  let lastActual = -1;
  ordered.forEach((milestone, i) => {
    if (milestone.actualDate) lastActual = i;
  });
  return index === lastActual || index === lastActual + 1;
}

/** The previous milestone's actual day, when it has one. */
export function previousActualDay(
  milestones: MilestoneNode[],
  milestoneId: string
): string | null {
  const ordered = milestonesInSequence(milestones);
  const index = ordered.findIndex((m) => m.id === milestoneId);
  const prev = index > 0 ? ordered[index - 1] : undefined;
  return prev?.actualDate ? dayOf(prev.actualDate) : null;
}

/** Today or earlier, and not before the previous milestone's actual date. */
export function isActualDayOutOfRange(
  day: string,
  today: string,
  previousActual: string | null
): boolean {
  if (day > today) return true;
  if (previousActual && day < previousActual) return true;
  return false;
}

/**
 * Same committed-date rules as the date block: not before the previous
 * milestone's actual or committed instant, and not outside the neighboring
 * committed days. Comparisons run in the project zone so the next calendar
 * day is not treated as still in range.
 */
export function isCommittedDateDisabled(
  current: Dayjs,
  milestones: MilestoneNode[],
  milestoneId: string
): boolean {
  const { ordered, index } = milestoneIndex(milestones, milestoneId);
  const prev = index > 0 ? ordered[index - 1] : undefined;
  const currLunch = DateUtils.dateTimeObj(current)
    .hour(13)
    .minute(23)
    .second(24);
  const dependent = prev?.actualDate || prev?.committedDate;
  if (dependent && currLunch.isBefore(DateUtils.dateTimeObj(dependent))) {
    return true;
  }

  const currDay = DateUtils.dateTimeObj(current).startOf("day");
  const past = latestPastCommittedDate(milestones, milestoneId);
  if (past && currDay.isBefore(DateUtils.dateTimeObj(past).startOf("day"))) {
    return true;
  }
  const future = earliestFutureCommittedDate(milestones, milestoneId);
  if (future && currDay.isAfter(DateUtils.dateTimeObj(future).startOf("day"))) {
    return true;
  }
  return false;
}

/**
 * Same actual-date rules as the date block: not after now, and not before the
 * previous milestone's actual date. Both sides are read in the project zone.
 */
export function isActualDateDisabled(
  current: Dayjs,
  milestones: MilestoneNode[],
  milestoneId: string
): boolean {
  const after = DateUtils.dateTimeObj(current).isAfter(DateUtils.dateTimeObj());
  const { ordered, index } = milestoneIndex(milestones, milestoneId);
  const prev = index > 0 ? ordered[index - 1] : undefined;
  if (!prev?.actualDate) return after;

  const currLunch = DateUtils.dateTimeObj(current).hour(13).minute(0).second(0);
  const before = currLunch.isBefore(DateUtils.dateTimeObj(prev.actualDate));
  return before || after;
}
